import React, {useEffect, useRef, useState} from 'react'
import {createPortal} from "react-dom";
import {Button} from "primereact/button";
import {Chart} from "primereact/chart";
import FetchUtil from "../../../../util/FetchUtil";
import ConfigUtil from "../../../../util/ConfigUtil";
import DateUtil from "../../../../util/DateUtil";
import {SpielerStatistikInhalt} from "./StatistikSpielerDialog";
import {FARBEN, getAchsen, getDiagrammOptionen, useSchmaleAnsicht, vorhanden, zahl} from "./statistikWerte";

/**
 * Details zu einem einzelnen Spiel (Game/Details) im neuen Design, als Vollbild über der Seite.
 * Ersetzt in der neuen Ansicht den StatistikGameDialog; Daten wie dort über gameausarchiv.
 *
 * @param gameId Archiv-Id des Games, alternativ tournamentId + tournamentGameId
 * @param ergebnisVomScorer false, wenn das Ergebnis von Hand eingetragen wurde, dann gibt es keine Details
 */
export default function SpielDetailsDialog({gameId, tournamentId, tournamentGameId, ergebnisVomScorer, onClose}) {

    const [game, setGame] = useState(undefined);
    const [fehler, setFehler] = useState(undefined);
    const [ansicht, setAnsicht] = useState("legs");

    useEffect(() => {
        // die Seite dahinter soll nicht mitscrollen
        const overflowVorher = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = overflowVorher;
        };
    }, []);

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === "Escape") {
                onClose();
            }
        };
        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [onClose]);

    useEffect(() => {
        if (!ergebnisVomScorer) {
            return;
        }
        setFehler(undefined);
        setGame(undefined);
        const basis = ConfigUtil.getConfig().resourceUrls.public + "/gameausarchiv/x01/";
        const url = gameId !== undefined
            ? basis + gameId + "/gameid"
            : basis + tournamentId + "/" + tournamentGameId + "/tournamentgameid";
        FetchUtil.fetchGet(url,
            json => setGame(json),
            responseNotOk => setFehler("Fehler beim Laden der Gamedaten: " + responseNotOk.message),
            () => setFehler("Allgemeiner Fehler beim Laden der Gamedaten"));
    }, [gameId, tournamentId, tournamentGameId, ergebnisVomScorer]);

    const name1 = game ? game.player1Name : undefined;
    const name2 = game ? (game.player2Name ? game.player2Name : "Gast") : undefined;

    const getInhalt = () => {
        if (!ergebnisVomScorer) {
            return <p className="dhpt-hinweis">Das Ergebnis zum Spiel wurde von Hand eingetragen, keine Details vorhanden.</p>;
        }
        if (fehler) {
            return <p className="dhpt-hinweis dhpt-gd-fehler" role="alert">{fehler}</p>;
        }
        if (!game) {
            return <p className="dhpt-hinweis">Spieldetails werden geladen …</p>;
        }
        const legs = ermittleLegs(game);
        const isSetModus = legs.some(leg => leg.setNumber > 1);
        return <>
            <Kopf game={game} name1={name1} name2={name2} isSetModus={isSetModus}/>
            <div className="dhpt-segment dhpt-gd-auswahl" role="radiogroup" aria-label="Ansicht">
                {[{id: "legs", label: "Legs"}, {id: "player1", label: name1}, {id: "player2", label: name2}].map(e =>
                    <button key={e.id} type="button" role="radio" aria-checked={ansicht === e.id} className="dhpt-segment-knopf"
                            onClick={() => setAnsicht(e.id)}>{e.label}</button>)}
            </div>
            {ansicht === "legs" && <LegsAnsicht game={game} legs={legs} name1={name1} name2={name2} isSetModus={isSetModus}/>}
            {ansicht === "player1" && <SpielerStatistikInhalt statistik={game.player1Statistik} mitBilanz={false}/>}
            {ansicht === "player2" && <SpielerStatistikInhalt statistik={game.player2Statistik} mitBilanz={false}/>}
        </>
    }

    const dialog = <div className="dhpt-vollbild" role="dialog" aria-modal="true" aria-label="Spieldetails">
        <div className="dhpt-vollbild-leiste">
            <span className="dhpt-vollbild-titel">{game ? name1 + " vs. " + name2 : "Spieldetails"}</span>
            <Button icon="pi pi-times" label="Schließen" className="p-button-sm p-button-secondary" onClick={() => onClose()}/>
        </div>
        <div className="dhpt-vollbild-inhalt">
            <div className="dhpt-st-dialog dhpt-gd">
                {getInhalt()}
            </div>
        </div>
    </div>;

    // Direkt in die Seite hängen: aufgerufen wird der Dialog z.B. auch aus dem gezoomten Turnierbaum,
    // dort würde er sonst mitgezoomt und die Stile der alten Kacheln erben. In .dhpt-seite bleiben die Farbvariablen erhalten.
    const ziel = document.querySelector(".dhpt-seite");
    return ziel ? createPortal(dialog, ziel) : dialog;
}

/**
 * Legs des Games in Spielreihenfolge, je Leg die Details und Aufnahmen beider Spieler.
 */
function ermittleLegs(game) {
    const scoresZu = (gamedetailId) => {
        const eintrag = (game.scores || []).find(s => s.gamedetailId === gamedetailId);
        return eintrag ? eintrag.scores : [];
    };
    return [...game.gameDetailsPlayer1]
        .sort((a, b) => a.setNumber - b.setNumber || a.leg - b.leg)
        .map(detail1 => {
            const detail2 = game.gameDetailsPlayer2.find(d => d.setNumber === detail1.setNumber && d.leg === detail1.leg) || {};
            const scores1 = scoresZu(detail1.id);
            const scores2 = scoresZu(detail2.id);
            return {
                key: detail1.setNumber + "_" + detail1.leg, setNumber: detail1.setNumber, leg: detail1.leg,
                detail1, detail2, scores1, scores2,
                ausgebullt: (detail1.gewonnen && detail1.ausgebullt) || (detail2.gewonnen && detail2.ausgebullt),
            };
        });
}

function legBezeichnung(leg, isSetModus, kurz) {
    if (isSetModus) {
        return kurz ? "S" + leg.setNumber + "/L" + leg.leg : "Set " + leg.setNumber + " · Leg " + leg.leg;
    }
    return kurz ? "" + leg.leg : "Leg " + leg.leg;
}

function restScore(scores) {
    const letzter = scores[scores.length - 1];
    return letzter !== undefined && vorhanden(letzter.togo) ? letzter.togo : "–";
}

function formatDauer(dauerInMinuten) {
    if (dauerInMinuten < 60) {
        return dauerInMinuten + " Min.";
    }
    const minuten = dauerInMinuten % 60;
    return Math.floor(dauerInMinuten / 60) + " Std. " + (minuten < 10 ? "0" : "") + minuten + " Min.";
}

function Kopf({game, name1, name2, isSetModus}) {
    const anzahl1 = isSetModus ? game.player1Statistik.gewonneneSets : game.player1Statistik.gewonneneLegs;
    const anzahl2 = isSetModus ? game.player2Statistik.gewonneneSets : game.player2Statistik.gewonneneLegs;
    const sieger1 = anzahl1 > anzahl2;
    const sieger2 = anzahl2 > anzahl1;
    // ältere Games haben keine Zeiten, deshalb wird jedes Feld nur angezeigt, wenn es vorhanden ist
    const zeiten = [];
    if (vorhanden(game.startzeitpunkt)) {
        zeiten.push({label: "Start", wert: DateUtil.localDateTimeToString(game.startzeitpunkt)});
    }
    if (vorhanden(game.endezeitpunkt)) {
        zeiten.push({label: "Ende", wert: DateUtil.localDateTimeToString(game.endezeitpunkt)});
    }
    if (vorhanden(game.dauer)) {
        zeiten.push({label: "Dauer", wert: formatDauer(game.dauer)});
    }
    return <section className="dhpt-karte dhpt-gd-kopf">
        <div className="dhpt-gd-ergebnis">
            <span className={"dhpt-gd-name dhpt-gd-name-links" + (sieger1 ? " dhpt-gd-sieger" : "")}>{name1}</span>
            <span className="dhpt-gd-stand" aria-label={"Ergebnis " + anzahl1 + " zu " + anzahl2 + (isSetModus ? " Sets" : " Legs")}>
                {anzahl1}<span className="dhpt-gd-stand-trenner">:</span>{anzahl2}
            </span>
            <span className={"dhpt-gd-name" + (sieger2 ? " dhpt-gd-sieger" : "")}>{name2}</span>
        </div>
        <div className="dhpt-gd-avg">
            <span>Ø {zahl(game.player1Statistik.average)}</span>
            <span className="dhpt-gd-avg-label">Average</span>
            <span>Ø {zahl(game.player2Statistik.average)}</span>
        </div>
        {zeiten.length > 0 &&
            <ul className="dhpt-gd-zeiten">
                {zeiten.map(z => <li key={z.label}><span>{z.label}</span> {z.wert}</li>)}
            </ul>
        }
    </section>
}

function LegsAnsicht({game, legs, name1, name2, isSetModus}) {
    const s1 = game.player1Statistik;
    const s2 = game.player2Statistik;
    const schmal = useSchmaleAnsicht();

    const getVergleich = () => {
        const zeilen = [
            {label: "Average", w1: s1.average, w2: s2.average, format: w => zahl(w)},
            {label: "First 9 Average", w1: s1.first9Average, w2: s2.first9Average, format: w => zahl(w)},
            {label: "100+", w1: s1._100Plus, w2: s2._100Plus},
            {label: "140+", w1: s1._140Plus, w2: s2._140Plus},
            {label: "180", w1: s1._180er, w2: s2._180er},
            {label: "Best Leg (Darts)", w1: s1.bestLeg, w2: s2.bestLeg, kleinerBesser: true},
        ];
        const besser = (a, b, kleinerBesser) => vorhanden(a) && vorhanden(b) && Number(a) !== Number(b)
            && (kleinerBesser ? Number(a) < Number(b) : Number(a) > Number(b));
        if (schmal) {
            return getVergleichSchmal(zeilen, besser);
        }
        return <section className="dhpt-karte">
            <h3 className="dhpt-karte-titel"><span>Vergleich</span></h3>
            <table className="dhpt-tab dhpt-gd-vergleich">
                <caption className="dhpt-st-versteckt">Vergleich der Spieler</caption>
                <thead>
                <tr>
                    <th scope="col" className="dhpt-tab-zahl">{name1}</th>
                    <th scope="col" className="dhpt-gd-mitte"><span className="dhpt-st-versteckt">Wert</span></th>
                    <th scope="col">{name2}</th>
                </tr>
                </thead>
                <tbody>
                {zeilen.map(z => {
                    const format = z.format || (w => vorhanden(w) ? w : "–");
                    return <tr key={z.label} className="dhpt-tab-zeile">
                        <td className={"dhpt-tab-zahl" + (besser(z.w1, z.w2, z.kleinerBesser) ? " dhpt-gd-besser" : "")}>{format(z.w1)}</td>
                        <th scope="row" className="dhpt-gd-mitte">{z.label}</th>
                        <td className={besser(z.w2, z.w1, z.kleinerBesser) ? "dhpt-gd-besser" : undefined}>{format(z.w2)}</td>
                    </tr>
                })}
                </tbody>
            </table>
        </section>
    }

    // Handy: Head-to-Head-Liste statt Tabelle; je Kennzahl die Werte links/rechts, die Bezeichnung in der Mitte
    // und darunter ein geteilter Balken in den Spielerfarben, der das Verhältnis zeigt
    const getVergleichSchmal = (zeilen, besser) => {
        const anteil1 = (w1, w2, kleinerBesser) => {
            let a = Number(w1);
            let b = Number(w2);
            if (!vorhanden(w1) || !vorhanden(w2) || isNaN(a) || isNaN(b)) {
                return 50;
            }
            if (kleinerBesser) {
                // weniger Darts ist besser, daher Kehrwerte vergleichen
                a = a > 0 ? 1 / a : 0;
                b = b > 0 ? 1 / b : 0;
            }
            return a + b > 0 ? a / (a + b) * 100 : 50;
        };
        return <section className="dhpt-karte">
            <h3 className="dhpt-karte-titel"><span>Vergleich</span></h3>
            <div className="dhpt-gd-h2h-namen">
                <span className="dhpt-gd-h2h-name dhpt-gd-h2h-name-a">{name1}</span>
                <span className="dhpt-gd-h2h-name dhpt-gd-h2h-name-b">{name2}</span>
            </div>
            <ul className="dhpt-gd-h2h">
                {zeilen.map(z => {
                    const format = z.format || (w => vorhanden(w) ? w : "–");
                    const links = anteil1(z.w1, z.w2, z.kleinerBesser);
                    return <li key={z.label} className="dhpt-gd-h2h-zeile">
                        <div className="dhpt-gd-h2h-werte">
                            <span className={"dhpt-gd-h2h-wert" + (besser(z.w1, z.w2, z.kleinerBesser) ? " dhpt-gd-besser" : "")}>
                                <span className="dhpt-st-versteckt">{name1}: </span>{format(z.w1)}
                            </span>
                            <span className="dhpt-gd-h2h-label">{z.label}</span>
                            <span className={"dhpt-gd-h2h-wert dhpt-gd-h2h-wert-b" + (besser(z.w2, z.w1, z.kleinerBesser) ? " dhpt-gd-besser" : "")}>
                                <span className="dhpt-st-versteckt">{name2}: </span>{format(z.w2)}
                            </span>
                        </div>
                        <div className="dhpt-gd-h2h-balken" aria-hidden="true">
                            <span className="dhpt-gd-h2h-balken-a" style={{width: links + "%"}}/>
                            <span className="dhpt-gd-h2h-balken-b" style={{width: (100 - links) + "%"}}/>
                        </div>
                    </li>
                })}
            </ul>
        </section>
    }

    const getDiagramm = () => {
        const data = {
            labels: legs.map(leg => isSetModus ? "S" + leg.setNumber + "/L" + leg.leg : "Leg " + leg.leg),
            datasets: [
                {label: name1, data: legs.map(leg => leg.detail1.average), backgroundColor: FARBEN.spielerA, borderRadius: 4},
                {label: name2, data: legs.map(leg => leg.detail2.average), backgroundColor: FARBEN.spielerB, borderRadius: 4},
            ],
        };
        return <section className="dhpt-karte">
            <h3 className="dhpt-karte-titel"><span>Average je Leg</span></h3>
            <div className="dhpt-st-balken-diagramm" role="img"
                 aria-label={"Average je Leg: " + legs.map(leg => legBezeichnung(leg, isSetModus, false) + " " + name1 + " " + leg.detail1.average + ", " + name2 + " " + leg.detail2.average).join("; ")}>
                <Chart type="bar" data={data} style={{height: "100%"}} options={getDiagrammOptionen({scales: getAchsen("Average")})}/>
            </div>
        </section>
    }

    const checkWert = (detail, scores) => {
        if (detail.gewonnen) {
            return <b>{detail.ausgebullt ? "Bull" : detail.wuerfe}</b>;
        }
        return <span className="dhpt-gd-leise">({restScore(scores)})</span>;
    }

    // die drei Zellen eines Spielers; beim Leg-Gewinner farbig hinterlegt
    const legZellen = (detail, scores) => {
        const klasse = "dhpt-tab-zahl" + (detail.gewonnen ? " dhpt-gd-gewinner" : "");
        return <>
            <td className={klasse + (detail.gewonnen ? " dhpt-gd-gewinner-start" : "")}>
                {detail.gewonnen && <span className="dhpt-st-versteckt">Gewinner, </span>}
                {detail.average}
            </td>
            <td className={klasse}>{detail.first9Average}</td>
            <td className={klasse + (detail.gewonnen ? " dhpt-gd-gewinner-ende" : "")}>{checkWert(detail, scores)}</td>
        </>
    }

    // Handy: einfache Liste im Stil des Vergleichs – Namen einmal oben, je Leg ein Block mit
    // Average, Ø 9D und Darts (links Spieler 1, rechts Spieler 2); die Werte des Leg-Gewinners sind grün
    const getLegListeSchmal = () => {
        const zeile = (label, wert1, wert2, gewonnen1, gewonnen2) => <div className="dhpt-gd-ll-zeile">
            <span className={"dhpt-gd-ll-wert" + (gewonnen1 ? " dhpt-gd-besser" : "")}><span className="dhpt-st-versteckt">{name1}: </span>{wert1}</span>
            <span className="dhpt-gd-ll-label">{label}</span>
            <span className={"dhpt-gd-ll-wert dhpt-gd-ll-wert-b" + (gewonnen2 ? " dhpt-gd-besser" : "")}><span className="dhpt-st-versteckt">{name2}: </span>{wert2}</span>
        </div>;
        const dartsText = (detail, scores) => detail.gewonnen
            ? (detail.ausgebullt ? "Bull" : detail.wuerfe)
            : <span className="dhpt-gd-leise">({restScore(scores)})</span>;
        return <div className="dhpt-gd-ll">
            <div className="dhpt-gd-h2h-namen">
                <span className="dhpt-gd-h2h-name dhpt-gd-h2h-name-a">{name1}</span>
                <span className="dhpt-gd-h2h-name dhpt-gd-h2h-name-b">{name2}</span>
            </div>
            <ul className="dhpt-gd-ll-liste">
                {legs.map(leg => {
                    const g1 = leg.detail1.gewonnen === true;
                    const g2 = leg.detail2.gewonnen === true;
                    return <li key={leg.key} className="dhpt-gd-ll-leg">
                        <div className="dhpt-gd-ll-titel">
                            {legBezeichnung(leg, isSetModus, false)}
                            {(g1 || g2) && <span className="dhpt-st-versteckt">, gewonnen von {g1 ? name1 : name2}</span>}
                        </div>
                        {zeile("Average", leg.detail1.average, leg.detail2.average, g1, g2)}
                        {zeile("Ø 9D", leg.detail1.first9Average, leg.detail2.first9Average, g1, g2)}
                        {zeile("Darts", dartsText(leg.detail1, leg.scores1), dartsText(leg.detail2, leg.scores2), g1, g2)}
                    </li>
                })}
                <li className="dhpt-gd-ll-leg dhpt-gd-ll-gesamt">
                    <div className="dhpt-gd-ll-titel">Gesamt</div>
                    {zeile("Average", zahl(s1.average), zahl(s2.average), false, false)}
                    {zeile("Ø 9D", zahl(s1.first9Average), zahl(s2.first9Average), false, false)}
                </li>
            </ul>
        </div>
    }

    const getLegTabelle = () => {
        const dartsBisBullen = [];
        legs.filter(leg => leg.ausgebullt).forEach(leg => {
            // die erste Zeile der Scores ist die Startzeile, jede weitere Zeile ist eine Aufnahme mit 3 Darts
            const darts = (Math.max(leg.scores1.length, leg.scores2.length) - 1) * 3;
            if (!dartsBisBullen.includes(darts)) {
                dartsBisBullen.push(darts);
            }
        });
        return <section className="dhpt-karte">
            <h3 className="dhpt-karte-titel"><span>Legs</span><span className="dhpt-karte-zusatz">Average · First 9 · Checkdarts</span></h3>
            {schmal ? getLegListeSchmal() : <div className="dhpt-gd-scroll">
                <table className="dhpt-tab dhpt-gd-legs">
                    <caption className="dhpt-st-versteckt">Werte je Leg</caption>
                    <thead>
                    <tr>
                        <th scope="col" rowSpan={2}>{isSetModus ? "Set/Leg" : "Leg"}</th>
                        <th scope="colgroup" colSpan={3} className="dhpt-gd-spieler-kopf dhpt-gd-farbe-a">{name1}</th>
                        <th scope="colgroup" colSpan={3} className="dhpt-gd-spieler-kopf dhpt-gd-farbe-b">{name2}</th>
                    </tr>
                    <tr>
                        <th scope="col" className="dhpt-tab-zahl">Avg</th>
                        <th scope="col" className="dhpt-tab-zahl"><abbr title="First 9 Average">Ø 9D</abbr></th>
                        <th scope="col" className="dhpt-tab-zahl">Darts</th>
                        <th scope="col" className="dhpt-tab-zahl">Avg</th>
                        <th scope="col" className="dhpt-tab-zahl"><abbr title="First 9 Average">Ø 9D</abbr></th>
                        <th scope="col" className="dhpt-tab-zahl">Darts</th>
                    </tr>
                    </thead>
                    <tbody>
                    {legs.map(leg => <tr key={leg.key} className="dhpt-tab-zeile">
                        <th scope="row">{legBezeichnung(leg, isSetModus, true)}</th>
                        {legZellen(leg.detail1, leg.scores1)}
                        {legZellen(leg.detail2, leg.scores2)}
                    </tr>)}
                    <tr className="dhpt-tab-zeile dhpt-gd-gesamt">
                        <th scope="row">Gesamt</th>
                        <td className="dhpt-tab-zahl">{zahl(s1.average)}</td>
                        <td className="dhpt-tab-zahl">{zahl(s1.first9Average)}</td>
                        <td/>
                        <td className="dhpt-tab-zahl">{zahl(s2.average)}</td>
                        <td className="dhpt-tab-zahl">{zahl(s2.first9Average)}</td>
                        <td/>
                    </tr>
                    </tbody>
                </table>
            </div>}
            <p className="dhpt-hinweis dhpt-gd-fussnote">Der Gewinner des Legs ist {schmal ? "grün dargestellt" : "grün hinterlegt"}. In Klammern steht der Restscore beim Check.</p>
            {dartsBisBullen.length > 0 &&
                <p className="dhpt-hinweis dhpt-gd-fussnote">Bull: Das Leg wurde nach {dartsBisBullen.join(" bzw. ")} Darts je Spieler durch Ausbullen entschieden.</p>
            }
        </section>
    }

    return <>
        {getVergleich()}
        {legs.length > 0 && getDiagramm()}
        {legs.length > 0 && getLegTabelle()}
        {legs.length > 0 && <LegAuswahl legs={legs} isSetModus={isSetModus} name1={name1} name2={name2}/>}
    </>
}

/**
 * Aufnahmen je Leg: oben die Legs nebeneinander zur Auswahl (Standard ist das erste Leg),
 * mit Vor/Zurück lässt sich Leg für Leg weiterschalten. Darunter die Aufnahmen des gewählten Legs.
 */
function LegAuswahl({legs, isSetModus, name1, name2}) {
    const [index, setIndex] = useState(0);
    const knoepfe = useRef([]);
    const leiste = useRef(null);

    useEffect(() => {
        // gewähltes Leg in der Leiste sichtbar halten, falls diese scrollt; nur waagerecht, der Dialog selbst soll nicht springen
        const knopf = knoepfe.current[index];
        const container = leiste.current;
        if (!knopf || !container) {
            return;
        }
        // die Leiste ist position: relative, offsetLeft bezieht sich daher auf sie
        const links = knopf.offsetLeft;
        const rechts = links + knopf.offsetWidth;
        if (links < container.scrollLeft) {
            container.scrollLeft = links - 8;
        } else if (rechts > container.scrollLeft + container.clientWidth) {
            container.scrollLeft = rechts - container.clientWidth + 8;
        }
    }, [index]);

    const waehle = (neu, fokus) => {
        const begrenzt = Math.max(0, Math.min(legs.length - 1, neu));
        setIndex(begrenzt);
        if (fokus && knoepfe.current[begrenzt]) {
            knoepfe.current[begrenzt].focus();
        }
    };

    // Pfeiltasten in der Leiste wie bei einer Radiogruppe
    const handleKeyDown = (event) => {
        const schritte = {ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1};
        if (schritte[event.key] !== undefined) {
            event.preventDefault();
            waehle(index + schritte[event.key], true);
        } else if (event.key === "Home") {
            event.preventDefault();
            waehle(0, true);
        } else if (event.key === "End") {
            event.preventDefault();
            waehle(legs.length - 1, true);
        }
    };

    const leg = legs[Math.min(index, legs.length - 1)];

    return <section className="dhpt-karte dhpt-gd-aufnahmen">
        <h3 className="dhpt-karte-titel">
            <span>Aufnahmen je Leg</span>
            <span className="dhpt-karte-zusatz">{legBezeichnung(leg, isSetModus, false)} von {legs.length} {legs.length === 1 ? "Leg" : "Legs"}</span>
        </h3>
        <div className="dhpt-gd-leg-leiste">
            <button type="button" className="dhpt-gd-leg-schritt" aria-label="Vorheriges Leg" title="Vorheriges Leg"
                    disabled={index === 0} onClick={() => waehle(index - 1)}>
                <i className="pi pi-chevron-left" aria-hidden="true"/>
            </button>
            <div ref={leiste} className="dhpt-gd-leg-knoepfe" role="radiogroup" aria-label="Leg auswählen" onKeyDown={handleKeyDown}>
                {legs.map((l, i) => {
                    const farbe = l.detail1.gewonnen ? " dhpt-gd-leg-knopf-a" : l.detail2.gewonnen ? " dhpt-gd-leg-knopf-b" : "";
                    const sieger = l.detail1.gewonnen ? name1 : l.detail2.gewonnen ? name2 : undefined;
                    return <button key={l.key} ref={el => knoepfe.current[i] = el} type="button" role="radio" aria-checked={i === index}
                                   tabIndex={i === index ? 0 : -1} className={"dhpt-gd-leg-knopf" + farbe}
                                   title={legBezeichnung(l, isSetModus, false) + (sieger ? " – gewonnen von " + sieger : "")}
                                   onClick={() => waehle(i)}>
                        {legBezeichnung(l, isSetModus, true)}
                    </button>
                })}
            </div>
            <button type="button" className="dhpt-gd-leg-schritt" aria-label="Nächstes Leg" title="Nächstes Leg"
                    disabled={index === legs.length - 1} onClick={() => waehle(index + 1)}>
                <i className="pi pi-chevron-right" aria-hidden="true"/>
            </button>
        </div>
        <LegAufnahmen leg={leg} isSetModus={isSetModus} name1={name1} name2={name2}/>
    </section>
}

/**
 * Aufnahmen eines Legs: Score und Rest je Spieler, in der Mitte die Anzahl Darts.
 */
function LegAufnahmen({leg, isSetModus, name1, name2}) {
    const schmal = useSchmaleAnsicht();
    let scores1 = leg.scores1;
    let scores2 = leg.scores2;
    let anzahl = Math.max(scores1.length, scores2.length);
    if (leg.ausgebullt) {
        // Leg wurde durch die Bullabfrage beendet, beide Spieler erhalten eine zusätzliche Zeile
        anzahl = anzahl + 1;
    }
    const sieger = leg.detail1.gewonnen ? name1 : leg.detail2.gewonnen ? name2 : undefined;

    // kleiner grüner Haken vor dem Namen des Leg-Gewinners im Tabellenkopf
    const spielerKopf = (name, detail) => <>
        {detail.gewonnen && <><i className="pi pi-check-circle dhpt-gd-gewinner-icon" aria-hidden="true"/><span className="dhpt-st-versteckt">Gewinner: </span></>}
        {name}
    </>;

    const zelle = (scores, index, detail) => {
        if (leg.ausgebullt && index === anzahl - 1) {
            const bull = detail.gewonnen && detail.ausgebullt;
            return [<td key="s" className="dhpt-tab-zahl">{bull ? <b>Bull</b> : ""}</td>, <td key="r" className="dhpt-tab-zahl"/>];
        }
        const score = scores[index];
        if (!score) {
            return [<td key="s"/>, <td key="r"/>];
        }
        const mitWuerfen = score.wurf1 || score.wurf2 || score.wurf3;
        return [
            <td key="s" className={"dhpt-tab-zahl" + (score.scoregueltig === false ? " dhpt-gd-ungueltig" : "")}>
                {vorhanden(score.aufnahme) ? score.aufnahme : ""}
                {mitWuerfen && <span className="dhpt-gd-wuerfe">{[score.wurf1, score.wurf2, score.wurf3].join(" / ")}</span>}
            </td>,
            <td key="r" className="dhpt-tab-zahl dhpt-gd-rest">{score.togo}</td>,
        ];
    };

    const zeilen = [];
    for (let i = 0; i < anzahl; i++) {
        const ohneDarts = i === 0 || (leg.ausgebullt && i === anzahl - 1);
        zeilen.push(<tr key={i} className="dhpt-tab-zeile">
            {zelle(scores1, i, leg.detail1)}
            <td className="dhpt-gd-darts">{ohneDarts ? "" : i * 3}</td>
            {zelle(scores2, i, leg.detail2)}
        </tr>);
    }

    if (schmal) {
        // Handy: einfache Liste im Stil von Vergleich und Legs – Namen einmal oben, je Aufnahme eine Zeile
        const haelfte = (scores, index, detail, seite) => {
            const klasse = "dhpt-gd-al-seite dhpt-gd-al-seite-" + seite;
            if (leg.ausgebullt && index === anzahl - 1) {
                const bull = detail.gewonnen && detail.ausgebullt;
                return <span className={klasse}><span className="dhpt-gd-al-score">{bull ? "Bull" : ""}</span><span className="dhpt-gd-al-rest"/></span>;
            }
            const score = scores[index];
            if (!score) {
                return <span className={klasse}><span className="dhpt-gd-al-score"/><span className="dhpt-gd-al-rest"/></span>;
            }
            const mitWuerfen = score.wurf1 || score.wurf2 || score.wurf3;
            return <span className={klasse}>
                <span className={"dhpt-gd-al-score" + (score.scoregueltig === false ? " dhpt-gd-ungueltig" : "")}>
                    {vorhanden(score.aufnahme) ? score.aufnahme : ""}
                    {mitWuerfen && <span className="dhpt-gd-al-wuerfe">{[score.wurf1, score.wurf2, score.wurf3].join(" · ")}</span>}
                </span>
                <span className="dhpt-gd-al-rest">{score.togo}</span>
            </span>;
        };
        const listenZeilen = [];
        for (let i = 0; i < anzahl; i++) {
            const ohneDarts = i === 0 || (leg.ausgebullt && i === anzahl - 1);
            listenZeilen.push(<li key={i} className="dhpt-gd-al-zeile">
                {haelfte(scores1, i, leg.detail1, "a")}
                <span className="dhpt-gd-al-darts">{ohneDarts ? "" : i * 3}</span>
                {haelfte(scores2, i, leg.detail2, "b")}
            </li>);
        }
        return <div className="dhpt-gd-al">
            <div className="dhpt-gd-h2h-namen">
                <span className="dhpt-gd-h2h-name dhpt-gd-h2h-name-a">{spielerKopf(name1, leg.detail1)}</span>
                <span className="dhpt-gd-h2h-name dhpt-gd-h2h-name-b">{spielerKopf(name2, leg.detail2)}</span>
            </div>
            <div className="dhpt-gd-al-kopf" aria-hidden="true">
                <span className="dhpt-gd-al-seite dhpt-gd-al-seite-a"><span>Score</span><span>Rest</span></span>
                <span className="dhpt-gd-al-darts">Darts</span>
                <span className="dhpt-gd-al-seite dhpt-gd-al-seite-b"><span>Score</span><span>Rest</span></span>
            </div>
            <ul className="dhpt-gd-al-liste" aria-label={"Aufnahmen " + legBezeichnung(leg, isSetModus, false)}>{listenZeilen}</ul>
        </div>
    }

    return <div className="dhpt-gd-leg">
        <div className="dhpt-gd-leg-kopf">
            <span className="dhpt-gd-leg-titel">{legBezeichnung(leg, isSetModus, false)}</span>
            {sieger && <span className="dhpt-gd-leg-sieger">{sieger}</span>}
            <span className="dhpt-gd-leg-avg">Ø {leg.detail1.average} : {leg.detail2.average}</span>
        </div>
        <div className="dhpt-gd-scroll">
            <table className="dhpt-tab dhpt-gd-aufnahme-tab">
                <caption className="dhpt-st-versteckt">Aufnahmen {legBezeichnung(leg, isSetModus, false)}</caption>
                <thead>
                <tr>
                    <th scope="col" colSpan={2} className="dhpt-gd-spieler-kopf dhpt-gd-farbe-a">{spielerKopf(name1, leg.detail1)}</th>
                    <th scope="col" className="dhpt-gd-darts" rowSpan={2}>Darts</th>
                    <th scope="col" colSpan={2} className="dhpt-gd-spieler-kopf dhpt-gd-farbe-b">{spielerKopf(name2, leg.detail2)}</th>
                </tr>
                <tr>
                    <th scope="col" className="dhpt-tab-zahl">Score</th>
                    <th scope="col" className="dhpt-tab-zahl">Rest</th>
                    <th scope="col" className="dhpt-tab-zahl">Score</th>
                    <th scope="col" className="dhpt-tab-zahl">Rest</th>
                </tr>
                </thead>
                <tbody>{zeilen}</tbody>
            </table>
        </div>
    </div>
}
